import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { paymentService } from '../../services/paymentService';
import { useDealStore } from '../../store/dealStore';
import { RequestState } from '../../types';
import config from '../../config';
import { AnimatedIcon } from '../../components/icons';

const STATE_STEPS = [
  RequestState.ASSIGNED,
  RequestState.NEGOTIATING,
  RequestState.CONFIRMED,
  RequestState.CLOSED,
];

const STATE_LABELS: Record<string, string> = {
  [RequestState.ASSIGNED]: 'Assigned',
  [RequestState.NEGOTIATING]: 'Negotiating',
  [RequestState.CONFIRMED]: 'Confirmed',
  [RequestState.CLOSED]: 'Closed',
};

const COMMISSION_PAYMENT_NOTICE =
  'To pay your commission, use EcoCash or InnBucks on 0784 454 242. After payment, upload proof of payment and wait for admin approval.';

export const SurveyorDealDetail: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { currentDeal: deal, isLoading, error, fetchDealDetail } = useDealStore();
  const [uploadingPop, setUploadingPop] = useState(false);

  useEffect(() => {
    if (dealId) fetchDealDetail(dealId);
  }, [dealId]);

  // Fallback polling for deal status updates while on this page
  useEffect(() => {
    if (!dealId) return;
    const pollId = setInterval(() => fetchDealDetail(dealId), config.DEAL_POLL_INTERVAL);
    return () => clearInterval(pollId);
  }, [dealId]);

  const handlePopUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !dealId) return;

    setUploadingPop(true);
    try {
      await paymentService.submitPop({
        deal_id: dealId,
        proof_image: file,
      });
      alert('Proof of payment submitted successfully for admin review.');
      fetchDealDetail(dealId);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to upload POP.');
    } finally {
      setUploadingPop(false);
      e.target.value = '';
    }
  };

  if (isLoading || !deal) {
    return (
      <div style={s.center}>
        <div style={s.spinner} />
        <p style={{ color: '#636E72', marginTop: 12 }}>Loading deal…</p>
      </div>
    );
  }
  if (error) return <div style={s.center}><p style={{ color: '#E74C3C' }}>{error}</p></div>;

  const activeIdx = STATE_STEPS.indexOf(deal.status);
  const isConfirmed = deal.status === RequestState.CONFIRMED || deal.status === RequestState.CLOSED;
  const isChatLocked = deal.is_chat_blocked || deal.payment_status === 'PAID' || deal.status === RequestState.CLOSED;

  return (
    <div>
      <button style={s.backBtn} onClick={() => navigate(-1)}>← Back</button>
      <h1 style={s.title}>Deal #{deal.id.slice(0, 8)}</h1>

      {/* Timeline */}
      <div style={s.timeline}>
        {STATE_STEPS.map((step, i) => {
          const done = i <= activeIdx;
          return (
            <div key={step} style={s.timelineStep}>
              <div style={{ ...s.stepDot, background: done ? 'var(--sc-brand-primary)' : 'var(--sc-border)', color: done ? 'var(--sc-text-on-brand)' : 'var(--sc-text-secondary)' }}>
                {done ? '✓' : i + 1}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: done ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)', marginTop: 6 }}>
                {STATE_LABELS[step]}
              </div>
              {i < STATE_STEPS.length - 1 && <div style={{ ...s.stepLine, background: done ? 'var(--sc-brand-primary)' : 'var(--sc-border)' }} />}
            </div>
          );
        })}
      </div>

      <div style={s.gridTwo}>
        {/* Request details */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>Request Details</h3>
          <div style={s.fieldGrid}>
            <Field label="Service" value={deal.request?.service_type ?? '—'} />
            <Field label="Description" value={deal.request?.description ?? '—'} />
            <Field label="Location" value={deal.request?.address ?? '—'} />
            <Field label="Deadline" value={deal.request?.deadline || '—'} />
            <Field label="Preferred Date" value={deal.request?.preferred_date || '—'} />
            <Field label="Created" value={deal.request?.created_at ? new Date(deal.request.created_at).toLocaleDateString('en-ZW') : '—'} />
          </div>
        </div>

        {/* Client details */}
        <div>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Client</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
              <div style={s.avatar}>{deal.client?.first_name?.[0] || '?'}{deal.client?.last_name?.[0] || '?'}</div>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--sc-text-primary)' }}>{deal.client?.first_name} {deal.client?.last_name}</div>
                <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)' }}>Client</div>
              </div>
            </div>

            {isConfirmed && deal.client_contact ? (
              <div style={s.contactBox}>
                <div style={{ ...s.contactLabel, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AnimatedIcon name="phone" size={14} color="var(--sc-success-text)" />
                  <span>Contact Info (Deal Confirmed)</span>
                </div>
                {deal.client_contact.phone && <div style={s.contactVal}>{deal.client_contact.phone}</div>}
                {deal.client_contact.email && <div style={s.contactVal}>{deal.client_contact.email}</div>}
              </div>
            ) : (
              <div style={{ ...s.warningBox, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AnimatedIcon name="shield" size={16} color="var(--sc-warning-text)" />
                <span>Contact info will be revealed after deal is confirmed.</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ ...s.card, marginTop: 16 }}>
            <h3 style={s.cardTitle}>Actions</h3>
            {deal.status !== RequestState.REQUEST_SUBMITTED && (
              <Link to={`/surveyor/chat/${deal.id}`} style={{ 
                ...s.primaryBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: isChatLocked ? 'var(--sc-bg-input)' : 'var(--sc-brand-primary)',
                color: isChatLocked ? 'var(--sc-text-tertiary)' : 'var(--sc-text-on-brand)',
                border: isChatLocked ? '1px solid var(--sc-border)' : 'none',
                opacity: isChatLocked ? 0.7 : 1,
              }}>
                <AnimatedIcon name="chat" size={16} color={isChatLocked ? 'var(--sc-text-tertiary)' : 'var(--sc-text-on-brand)'} />
                <span>{isChatLocked ? 'Chat (Locked)' : 'Open Chat'}</span>
              </Link>
            )}
            {deal.status === RequestState.NEGOTIATING && (
              <div style={{ ...s.primaryBtn, background: 'var(--sc-warning)', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <AnimatedIcon name="clipboard" size={16} color="var(--sc-text-on-brand)" />
                <span>Deal Terms Under Negotiation</span>
              </div>
            )}
            {deal.status === RequestState.CONFIRMED && (
              <div style={{ ...s.successBox, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <AnimatedIcon name="success" size={16} color="var(--sc-success-text)" />
                <span>Deal confirmed — value: ${deal.deal_value?.toFixed(2) || '0.00'}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deal value / Commission info */}
      {deal.deal_value != null && (
        <div style={{ ...s.card, marginTop: 16 }}>
          <h3 style={s.cardTitle}>Financial Summary</h3>
          <div style={s.fieldGrid}>
            <Field label="Deal Value" value={`$${deal.deal_value.toFixed(2)}`} />
            <Field label="Commission Rate" value={`${((deal.commission_rate ?? 0) * 100).toFixed(0)}%`} />
            <Field label="Platform Commission" value={`$${(deal.expected_commission || 0).toFixed(2)}`} />
            <Field label="Your Revenue" value={`$${(deal.deal_value - (deal.expected_commission || 0)).toFixed(2)}`} />
          </div>
          <div style={s.paymentNotice}>
            <AnimatedIcon name="money" size={16} color="var(--sc-warning-text)" />
            <span>{COMMISSION_PAYMENT_NOTICE}</span>
          </div>

          {(deal.payment_status === 'UNPAID' || deal.payment_status === 'REJECTED') && (
            <div style={{ marginTop: 16 }}>
              <label style={{ ...s.primaryBtn, display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--sc-brand-primary)', cursor: 'pointer' }}>
                <AnimatedIcon name="folder" size={16} color="white" />
                <span>{uploadingPop ? 'Uploading...' : 'Upload Proof of Payment'}</span>
                <input type="file" style={{ display: 'none' }} accept="image/*" onChange={handlePopUpload} disabled={uploadingPop} />
              </label>
            </div>
          )}
          {deal.payment_status === 'PENDING_APPROVAL' && (
            <div style={{ ...s.successBox, marginTop: 16 }}>
              Proof of Payment is pending admin approval.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, color: 'var(--sc-text-primary)' }}>{value}</div>
  </div>
);

const s: Record<string, React.CSSProperties> = {
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'sc-spin 0.8s linear infinite' },
  backBtn: { background: 'none', border: 'none', fontSize: 14, color: 'var(--sc-text-secondary)', cursor: 'pointer', padding: 0, marginBottom: 12 },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 24px' },

  timeline: { display: 'flex', alignItems: 'flex-start', gap: 0, marginBottom: 32, background: 'var(--sc-bg-secondary)', borderRadius: 14, padding: '24px 20px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)', overflowX: 'auto' },
  timelineStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 80, position: 'relative' },
  stepDot: { width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, zIndex: 1 },
  stepLine: { position: 'absolute', top: 16, left: '50%', width: '100%', height: 3 },

  gridTwo: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 },
  card: { background: 'var(--sc-bg-secondary)', borderRadius: 14, padding: 24, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)', boxSizing: 'border-box' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px' },
  fieldGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px 24px' },
  avatar: { width: 44, height: 44, borderRadius: 12, background: 'var(--sc-brand-gradient)', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 },
  contactBox: { background: 'var(--sc-success-bg)', borderRadius: 10, padding: 14 },
  contactLabel: { fontSize: 12, fontWeight: 700, color: 'var(--sc-success-text)', marginBottom: 6 },
  contactVal: { fontSize: 14, color: 'var(--sc-text-primary)' },
  warningBox: { background: 'var(--sc-warning-bg)', border: '1px solid var(--sc-warning)', borderRadius: 10, padding: 14, fontSize: 13, color: 'var(--sc-warning-text)' },
  paymentNotice: { marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--sc-warning-bg)', border: '1px solid var(--sc-warning)', color: 'var(--sc-warning-text)', borderRadius: 10, padding: 14, fontSize: 13, fontWeight: 700, lineHeight: 1.45 },
  primaryBtn: { display: 'block', textAlign: 'center' as const, padding: '12px 20px', background: 'var(--sc-brand-primary)', color: 'var(--sc-text-on-brand)', borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: 'none', border: 'none', cursor: 'pointer' },
  successBox: { background: 'var(--sc-success-bg)', borderRadius: 10, padding: 14, fontSize: 14, color: 'var(--sc-success-text)', fontWeight: 600, textAlign: 'center' as const },
};
